import React, { useState, useEffect, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'helpers/highcharts-setup';
import Big from 'big.js';
import {
  AreaSeries,
  Chart,
  HighchartsChart,
  XAxis,
  YAxis,
  Tooltip,
  withHighcharts
} from 'react-jsx-highstock';
import { useTranslation } from 'react-i18next';
import Spinner from 'components/shared/Spinner';
import Text from 'components/shared/Text';
import { comparePropsIgnoringFunctions } from 'helpers/memo';
import styles from './styles.module.scss';
import { truncate } from '@nbx/frontend-helpers/format';

function DepthChart(props) {
  const [loading, setLoading] = useState(true);
  const [sells, setSells] = useState([]);
  const [buys, setBuys] = useState([]);
  const [midMarket, setMidMarket] = useState(props.marketPrice);
  const [maxQty, setMaxQty] = useState(0);
  const { t } = useTranslation();

  Highcharts.Tooltip.prototype.applyFilter = () => {};

  const processOrderBook = useCallback(() => {
    let meanBuyPrice = 0;
    let minSellPrice = Number.MAX_SAFE_INTEGER;
    let maxBuyPrice = 0;
    let compoundSellQty = 0;
    let compoundBuyQty = 0;
    let doubleStdDevBuyPrices = 0;
    let sellsArray = [];
    let buysArray = [];

    // Need to clone array so the .reverse() operations don't affect the orderbook elsewhere
    sellsArray = [...(props.orderBook.sells || [])].reverse().map(sell => {
      const floatPrice = parseFloat(sell.price);
      const floatQty = parseFloat(sell.quantity);
      if (floatPrice < minSellPrice) minSellPrice = floatPrice;
      compoundSellQty += floatQty;
      return [floatPrice, compoundSellQty, floatQty];
    });

    // ditto on the cloning
    buysArray = [...(props.orderBook.buys || [])]
      .map(buy => {
        const floatPrice = parseFloat(buy.price);
        const floatQty = parseFloat(buy.quantity);
        if (floatPrice > maxBuyPrice) maxBuyPrice = floatPrice;
        meanBuyPrice += floatPrice;
        compoundBuyQty += floatQty;
        return [floatPrice, compoundBuyQty, floatQty];
      })
      .reverse();

    meanBuyPrice /= buysArray.length;

    if (sellsArray.length > 10) {
      const devSellPrice = sellsArray[Math.round(sellsArray.length / 2)][0] - sellsArray[0][0];
      sellsArray = sellsArray.filter(
        sell => sell[0] <= props.marketPrice + devSellPrice && sell[0] >= props.marketPrice
      );
    }

    if (buysArray.length > 10) {
      const squareDiffsBuyPrices = [];
      buysArray.forEach(bid => squareDiffsBuyPrices.push((bid[0] - meanBuyPrice) ** 2));
      let meanOfSquareDiffsBuyPrices = squareDiffsBuyPrices.reduce((sum, squareDiff) => sum + squareDiff, 0);
      meanOfSquareDiffsBuyPrices /= squareDiffsBuyPrices.length;
      doubleStdDevBuyPrices = 2 * Math.sqrt(meanOfSquareDiffsBuyPrices);
      buysArray = buysArray.filter(
        buy => buy[0] >= props.marketPrice - doubleStdDevBuyPrices && buy[0] <= props.marketPrice
      );
    }

    let max = 0;
    sellsArray.forEach(sell => {
      if (sell[1] > max) [, max] = sell;
    });
    buysArray.forEach(buy => {
      if (buy[1] > max) [, max] = buy;
    });
    setSells(sellsArray);
    setBuys(buysArray);

    const midMarketPrice = Big(maxBuyPrice).plus(Big(minSellPrice).minus(maxBuyPrice).div(2));
    setMidMarket(truncate(midMarketPrice));
    setMaxQty(max);
    setLoading(false);
  }, [props.marketPrice, props.market.minTickLength, props.orderBook.buys, props.orderBook.sells]);

  useEffect(() => {
    if (!props.orderBook.loading && !props.orderBook.error && props.marketPrice !== null) {
      processOrderBook();
    }
  }, [processOrderBook, props.orderBook.loading, props.orderBook.error, props.marketPrice]);

  if (loading) {
    return <Spinner />;
  }

  function formatTooltipBySide(side) {
    return function formatTooltip() {
      // This function is called in highchart's context, so this refers to the chart and not the component
      /* eslint-disable react/no-this-in-sfc */
      const series = side === 'sells' ? sells : buys;
      const price = truncate(this.x, props.market.minTickLength);
      const limit = truncate(series[this.point.index]?.[2], props.market.minQtyLength);
      const sum = truncate(this.y, props.market.minQtyLength);
      const header = `<span class="${styles.tooltipText}">${t('common_price')}: ${price}</span>`;
      const value = `<span class="${styles.tooltipText}">${this.series.name}: ${limit}</span>`;
      const total = `<span class="${styles.tooltipText}">${t('common_total')}: ${sum}</span>`;
      /* eslint-enable */
      return `${header}<br/>${value}<br/>${total}`;
    };
  }

  if (props.marketPrice === null) {
    return (
      <div className={styles.fadeinWrap}>
        <div className={styles.chartNoData}>
          <Text>{t('market_not_enough_data_depth')}</Text>
        </div>
      </div>
    );
  }

  if (sells.length < 2 || buys.length < 2) {
    return (
      <div className={styles.fadeinWrap}>
        <div className={styles.chartNoData}>
          <Text>{t('market_not_enough_open_orders')}</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.fadeinWrap}>
      <div className={styles.midMarketPrice}>
        <Text>{midMarket}</Text>
        <Text muted>{t('market_mid_market_price')}</Text>
      </div>
      <div className="trade-chart-depth trade-chart-buys">
        {buys.length > 0 && (
          <HighchartsChart
            styledMode
            plotOptions={{
              area: {
                step: 'right',
                animation: false
              }
            }}>
            <Chart />
            <Tooltip formatter={formatTooltipBySide('buys')} />
            <XAxis min={buys[0][0]} max={buys[buys.length - 1][0]}>
              <AreaSeries name={t('common_buys')} data={buys} />
            </XAxis>
            <YAxis min={0} max={maxQty} />
          </HighchartsChart>
        )}
      </div>
      <div className="trade-chart-depth trade-chart-sells">
        {sells.length > 0 && (
          <HighchartsChart
            styledMode
            plotOptions={{
              area: {
                step: 'left',
                animation: false
              }
            }}>
            <Chart />
            <Tooltip formatter={formatTooltipBySide('sells')} />
            <XAxis min={sells[0][0]} max={sells[sells.length - 1][0]}>
              <AreaSeries name={t('common_sells')} data={sells} />
            </XAxis>
            <YAxis opposite min={0} max={maxQty} />
          </HighchartsChart>
        )}
      </div>
    </div>
  );
}

DepthChart.propTypes = {
  market: PropTypes.object.isRequired,
  orderBook: PropTypes.object.isRequired,
  marketPrice: PropTypes.number
  // isMobile: PropTypes.bool
};

DepthChart.defaultProps = {
  marketPrice: null
  // isMobile: false
};

export default memo(withHighcharts(DepthChart, Highcharts), comparePropsIgnoringFunctions);
